<template>
  <div>

    <div class="d-flex align-items-center mb-4 gap-2 flex-wrap">
      <transition name="fade">
        <div v-if="hasFilters" class="d-flex flex-wrap align-items-center gap-2">
          <!-- md+: individual removable chips -->
          <span v-if="searchTerm" class="d-none d-md-inline-flex badge bg-secondary rounded-pill align-items-center gap-1 pe-1 fw-normal">
            &ldquo;{{ searchTerm }}&rdquo;
            <button type="button" class="btn-close btn-close-white" style="font-size:0.55em" @click="searchTerm = ''" aria-label="Clear search" />
          </span>
          <span v-if="searchSource !== ''" class="d-none d-md-inline-flex badge bg-secondary rounded-pill align-items-center gap-1 pe-1 fw-normal">
            {{ sourceFilterLabel }}
            <button type="button" class="btn-close btn-close-white" style="font-size:0.55em" @click="searchSource = ''" aria-label="Clear source filter" />
          </span>
          <span v-if="soldFilter !== 'all'" class="d-none d-md-inline-flex badge bg-secondary rounded-pill align-items-center gap-1 pe-1 fw-normal text-capitalize">
            {{ soldFilter }}
            <button type="button" class="btn-close btn-close-white" style="font-size:0.55em" @click="soldFilter = 'all'" aria-label="Clear status filter" />
          </span>
          <span v-if="showHidden" class="d-none d-md-inline-flex badge bg-secondary rounded-pill align-items-center gap-1 pe-1 fw-normal">
            Including Hidden
            <button type="button" class="btn-close btn-close-white" style="font-size:0.55em" @click="showHidden = false" aria-label="Clear hidden filter" />
          </span>
          <button class="d-none d-md-inline btn btn-link btn-sm p-0 text-danger text-decoration-none" @click="clearAllFilters">Clear all</button>

          <!-- mobile: single collapsed chip -->
          <span class="d-inline-flex d-md-none badge bg-secondary rounded-pill align-items-center gap-1 pe-1 fw-normal">
            Filtered ({{ activeFilterCount }})
            <button type="button" class="btn-close btn-close-white" style="font-size:0.55em" @click="clearAllFilters" aria-label="Clear all filters" />
          </span>
        </div>
      </transition>

      <input-radio-group
          v-model="soldFilter"
          :options="soldFilterOptions"
          class="ms-auto"
          name="sold-filter" />
    </div>


    <div class="row g-3 mb-3">
      <div class="col-md-8">
        <input type="search" v-model="searchTerm" placeholder="Search Title or SKU" class="form-control" @search="handleSearchClear">
      </div>
      <div class="col-md-4">
        <source-categories v-model="searchSource" :enabled="true" />
      </div>
    </div>


    <div class="d-grid gap-4 d-flex flex-wrap align-items-center mb-3">
      <toggle-component label="Batch Edit" :toggled="showSelect" @update:toggled="toggleSelect" />
      <toggle-component class="ms-auto" label="Include hidden" :toggled="showHidden" @update:toggled="showHiddenToggle" />
      <toggle-component v-if="isUniqueSku" label="Group SKUs" :toggled="groupedSkus" @update:toggled="toggleGroupedSkus" />
    </div>



    <div v-if="showSelect" class="small d-flex align-items-center gap-3">
      <button class="btn btn-link btn-sm" :disabled="selectedIds.length===0" @click="showBatch=true">Update {{ selectedIds.length }} items</button>
      <span v-if="batchMessage" class="text-success small">{{ batchMessage }}</span>
    </div>

    <app-modal :modal-in="showBatch" @close="showBatch = false" modal-class="modal-md">
      <template #header>Batch Edit Source Details</template>
      <batch-source-edit
          :items="selectedIds"
          :source-price="batchPrefill.sourcePrice"
          :source-date="batchPrefill.sourceDate"
          :source-category-id="batchPrefill.sourceCategoryId"
          :source-location-id="batchPrefill.sourceLocationId"
          @cancel:source="showBatch = false"
          @update:source="batchSave"
          class="mb-3" />
      <div class="p-5 mb-5"></div>
    </app-modal>

    <hr>

    <paginator-bar
        :smooth="false"
        :total-items="totalItems"
        :per-page="limit"
        :page="page"
        variant="top"
        @next-page="nextPage" />

    <transition mode="out-in">
      <div v-if="fetching" class="group-list">
        <div class="group-list-item" v-for="i in 5" :key="i">
          <div class="card-body d-flex align-items-center justify-content-center" style="height: 140px;">
            <loading-spinner style="opacity: 0.5;"/>
          </div>
        </div>
      </div>

      <div v-else-if="filteredInventory.length === 0" class="alert alert-info p-5 mb-3 text-center">
        <template v-if="hasFilters">
          <strong>No matches found.</strong>
          Try a different search or clear filters.
        </template>

        <template v-else>
          <strong>No items yet.</strong>
          Items will appear here after your first import or manual entry.
        </template>


      </div>

      <div v-else class="list-group">
        <div v-for="inv in filteredInventory" :key="inv.id" class="list-group-item list-group-item-action p-0">
          <inventory-item
              :item="inv"
              @on-updated="fetchInventory"
              :showSelectBox="showSelect"
              :selected="selectedIds.indexOf(inv.id) >= 0"
              @on-selected="(checked) => updateList(inv.id, checked)"
              />
        </div>
      </div>
    </transition>
    <div class="mb-3 card bg-light" v-for="i in Math.min(5, Math.max(0, 5 - filteredInventory.length))" :key="i">
      <div class="card-body d-flex align-items-center justify-content-center" style="height: 140px;">
      </div>
    </div>
    <paginator-bar :smooth="false" :total-items="totalItems" :per-page="limit" :page="page" @next-page="nextPage" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import InventoryItem from "@/app/components/inventory/InventoryItem.vue";
import PaginatorBar from "@/app/components/Utils/PaginatorBar.vue";
import ToggleComponent from "@/app/components/Utils/ToggleComponent.vue";
import InputRadioGroup from "@/app/components/Utils/InputRadioGroup.vue";
import SourceCategories from "@/app/components/inventory/SourceCategories.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
import BatchSourceEdit from "@/app/components/inventory/BatchSourceEdit.vue";

const store = useStore()
const route = useRoute()

const fetching = ref(false)
const searchTerm = ref(route.query.search ?? '')
const searchSource = ref(route.query.sourceCategory ?? '')
const limit = ref(50)
const page = ref(1)
const soldFilter = ref('all')
const showHidden = ref(false)
const showSelect = ref(false)
const selectedIds = ref([])
const showBatch = ref(false)
const batchMessage = ref('')
const totalItems = ref(0)
const totalPages = ref(0)
const groupedSkus = ref(sessionStorage.getItem('esc-grouped-skus') === 'true')
const inventory = computed(() => store.state.eshop.inventory || [])
const filteredInventory = computed(() => inventory.value)
const hasFilters = computed(() => !!(searchTerm.value || searchSource.value !== '' || soldFilter.value !== 'all' || showHidden.value))
const uniqueSku = computed(() => store.state.eshop.eshop?.config?.UNIQUE_SKU)

const sourceFilterLabel = computed(() => {
  if (searchSource.value === '') return ''
  if (searchSource.value == '0') return 'Source: Missing'
  const cats = store.state.eshop.sourceCategories || []
  for (const cat of cats) {
    const sub = (cat.subcategories || []).find(s => String(s.id) === String(searchSource.value))
    if (sub) return `Source: ${sub.name}`
  }
  return 'Source: Applied'
})

const activeFilterCount = computed(() => [
  !!searchTerm.value,
  searchSource.value !== '',
  soldFilter.value !== 'all',
  showHidden.value,
].filter(Boolean).length)
const isUniqueSku = computed(() => uniqueSku.value === 'true')

const soldFilterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All' },
  { value: 'sold', label: 'Sold' },
]

const batchPrefill = computed(() => {
  const selected = inventory.value.filter(i => selectedIds.value.includes(i.id))
  const last = (field) => {
    const vals = selected.map(i => i[field]).filter(v => v !== null && v !== undefined && v !== 0 && v !== '')
    return vals.length ? vals[vals.length - 1] : null
  }
  return {
    sourcePrice: last('source_price'),
    sourceDate: last('acquisition_date'),
    sourceCategoryId: last('source_category_id'),
    sourceLocationId: last('source_location_id'),
  }
})

function toggleGroupedSkus(val) {
  groupedSkus.value = val
  sessionStorage.setItem('esc-grouped-skus', val)
  page.value = 1
  fetchInventory()
}

function handleSearchClear() {
  searchTerm.value = ''
  page.value = 1
}

async function fetchInventory() {
  fetching.value = true
  try {
    const data = await store.dispatch('eshop/getInventory', {
      page: page.value,
      limit: limit.value,
      search: searchTerm.value,
      soldFilter: soldFilter.value,
      sourceCategory: searchSource.value,
      showHidden: showHidden.value,
      groupedSkus: groupedSkus.value && isUniqueSku.value
    })
    totalItems.value = data.total
    totalPages.value = data.totalPages
  } catch (error) {
    console.error('Error fetching inventory:', error)
  } finally {
    fetching.value = false
  }
}

function toggleSelect(isShow) {
  showSelect.value = isShow
  if (!isShow) {
    selectedIds.value = []
  }
}

function updateList(id, selected) {
  selectedIds.value = selectedIds.value.filter(l => l !== id)
  if (selected) selectedIds.value = [...selectedIds.value, id]
}

async function batchSave( newSource ) {
  try {
    const result = await store.dispatch('eshop/batchUpdateSource', {
      ids: selectedIds.value,
      fields: newSource
    })
    showBatch.value = false
    batchMessage.value = `${result.updated} item${result.updated === 1 ? '' : 's'} updated`
    setTimeout(() => {
      batchMessage.value = ''
      toggleSelect(false)
    }, 3000)
    await fetchInventory()
  } catch (error) {
    console.error('Batch update failed:', error)
  }
}

function showHiddenToggle(isToggled) {
  showHidden.value = isToggled
  page.value = 1
}

function clearAllFilters() {
  searchTerm.value = ''
  searchSource.value = ''
  soldFilter.value = 'all'
  showHidden.value = false
  page.value = 1
}

async function nextPage(dir) {
  page.value += dir
  await fetchInventory()
}

// Watch for search term changes with debounce
let searchTimeout
watch(() => route.query.search, (val) => {
  const incoming = val ?? ''
  if (incoming !== searchTerm.value) {
    searchTerm.value = incoming
    page.value = 1
  }
})

watch(searchTerm, () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    fetchInventory()
  }, 300)
})

// Watch for source category changes
watch(searchSource, () => {
  page.value = 1
  fetchInventory()
})

// Watch for sold filter changes
watch(soldFilter, () => {
  page.value = 1
  fetchInventory()
})

// Watch for hidden toggle changes
watch(showHidden, () => {
  page.value = 1
  fetchInventory()
})

// Initial fetch on component mount
fetchInventory()

</script>

<style lang="scss" scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
